import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { BiNews } from 'react-icons/bi';
import api from 'services/client/api';
import { NewsArticle } from 'constants/stockNewsApi';
import Card from 'components/Card';
import SectionTitle from 'components/SectionTitle';
import Dropdown from 'components/Dropdown';

import { NewsItem, InlineSentiment } from './styles';

interface TickerNews {
  latest: NewsArticle[];
  top: NewsArticle[];
}

const GRAPH_FILTERS = [
  {
    name: 'Latest',
  },
  {
    name: 'Trending',
  },
];

const TickerNewsList: React.FC<React.PropsWithChildren<{ activeTicker: string }>> = ({ activeTicker }) => {
  const [latestNews, setLatestNews] = React.useState<NewsArticle[]>([]);
  const [topNews, setTopNews] = React.useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeGraph, setActiveGraph] = React.useState(0);
  const tickerNews = activeGraph === 0 ? latestNews : topNews;
  const aggregateSentiment = {
    positive: 0,
    negative: 0,
    neutral: 0,
  };

  tickerNews.forEach((item) => {
    if (item.sentiment === 'Positive') {
      aggregateSentiment.positive++;
    } else if (item.sentiment === 'Negative') {
      aggregateSentiment.negative++;
    } else if (item.sentiment === 'Neutral') {
      aggregateSentiment.neutral++;
    }
  });

  const { positive, negative, neutral } = aggregateSentiment;
  const hasSentiment = !!positive || !!negative || !!neutral;

  React.useEffect(() => {
    if (activeTicker) {
      setIsLoading(true);
      api
        .get(`tickers/news/${activeTicker}`)
        .then((resp: TickerNews) => {
          setIsLoading(false);
          if (resp?.latest?.length > 0) {
            setLatestNews(resp.latest);
            setTopNews(resp.top);
          } else {
            setLatestNews([]);
            setTopNews([]);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          Sentry.withScope((_scope) => {
            Sentry.captureException(err);
          });
          setLatestNews([]);
          setTopNews([]);
        });
    }
  }, [activeTicker]);

  return (
    <Card className="mb-2 flex h-full flex-col overflow-hidden pb-0" isDark>
      <SectionTitle
        Icon={BiNews}
        title={`${activeTicker || 'Ticker'} News`}
        right={
          <Dropdown
            triggerText={GRAPH_FILTERS[activeGraph].name}
            items={GRAPH_FILTERS.map((f, i) => ({
              ...f,
              onClick: () => setActiveGraph(i),
            }))}
          />
        }
      />
      {!isLoading ? (
        <>
          <div className="flow-grow flex-shrink overflow-auto">
            {tickerNews.map((news) => {
              const date = new Date(news.date);
              return (
                <NewsItem
                  key={news.news_url}
                  href={news.news_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="text mb-1 text-xs font-semibold">{news.title}</div>
                  <p className="mb-1 text-xs text-gray-400">
                    <InlineSentiment sentiment={news.sentiment}>{news.sentiment}</InlineSentiment>{' '}
                    {news.source_name} on {date.toLocaleDateString()}{' '}
                    {date.toLocaleTimeString(navigator.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  <p className="text-xs text-gray-400">{news.text}</p>
                </NewsItem>
              );
            })}
          </div>
          {hasSentiment && (
            <div className="flex items-center justify-center py-2 text-xs">
              <div className="mr-4">
                <span className="mr-1">{positive}</span>{' '}
                <InlineSentiment sentiment="Positive">Positive</InlineSentiment>
              </div>
              <div className="mr-4">
                <span className="mr-1">{neutral}</span>{' '}
                <InlineSentiment sentiment="Neutral">Neutral</InlineSentiment>
              </div>
              <div>
                <span className="mr-1">{negative}</span>{' '}
                <InlineSentiment sentiment="Negative">Negative</InlineSentiment>
              </div>
            </div>
          )}
        </>
      ) : (
        <div></div>
      )}
    </Card>
  );
};

export default TickerNewsList;
